// Gerenciamento das rádios do player de música.
// CRUD + reordenar (setas) + toggle enable + detectar metadata da URL.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Loader2,
  Music,
  Pencil,
  Plus,
  Search,
  Trash2,
  X
} from "lucide-react";
import { Button } from "@app/ui/components/button";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

// =====================
// Types + API
// =====================

interface Station {
  id: string;
  name: string;
  category: string;
  genre: string | null;
  url: string;
  isEnabled: boolean;
  sortOrder: number;
  somaChannelId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DetectResult {
  ok: boolean;
  name?: string;
  category?: string;
  genre?: string;
  somaChannelId?: string;
  message?: string;
}

async function apiCall<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: init.body ? { "content-type": "application/json" } : undefined,
    ...init
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      window.location.href = "/auth/";
      throw new Error("unauthenticated");
    }
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message ?? `Erro ${res.status}`);
  }
  return res.json();
}

const listStations = () =>
  apiCall<{ stations: Station[] }>("/api/manager/player-stations");
const createStation = (body: Partial<Station>) =>
  apiCall<{ station: Station }>("/api/manager/player-stations", {
    method: "POST",
    body: JSON.stringify(body)
  });
const updateStation = (id: string, body: Partial<Station>) =>
  apiCall<{ station: Station }>(`/api/manager/player-stations?id=${id}`, {
    method: "PATCH",
    body: JSON.stringify(body)
  });
const deleteStation = (id: string) =>
  apiCall(`/api/manager/player-stations?id=${id}`, { method: "DELETE" });
const reorderStations = (orderedIds: string[]) =>
  apiCall(`/api/manager/player-stations?action=reorder`, {
    method: "POST",
    body: JSON.stringify({ orderedIds })
  });
const detectStation = (url: string) =>
  apiCall<DetectResult>(`/api/manager/player-stations?action=detect`, {
    method: "POST",
    body: JSON.stringify({ url })
  });

// =====================
// State
// =====================

const stations = signal<Station[]>([]);
const loading = signal(true);
const search = signal("");
const editingId = signal<string | null>(null);
const creating = signal(false);

async function reload() {
  loading.value = true;
  try {
    const res = await listStations();
    stations.value = res.stations;
  } catch (err) {
    console.error("[player-stations] reload falhou:", err);
  } finally {
    loading.value = false;
  }
}

async function moveStation(id: string, delta: -1 | 1) {
  const list = [...stations.value];
  const idx = list.findIndex((s) => s.id === id);
  const target = idx + delta;
  if (idx < 0 || target < 0 || target >= list.length) return;
  // Swap local
  [list[idx], list[target]] = [list[target], list[idx]];
  stations.value = list;
  try {
    await reorderStations(list.map((s) => s.id));
  } catch (err) {
    console.error("[player-stations] reorder falhou:", err);
    await reload();
  }
}

async function toggleEnabled(s: Station) {
  // Optimistic
  stations.value = stations.value.map((x) =>
    x.id === s.id ? { ...x, isEnabled: !x.isEnabled } : x
  );
  try {
    await updateStation(s.id, { isEnabled: !s.isEnabled });
  } catch (err) {
    console.error("[player-stations] toggle falhou:", err);
    await reload();
  }
}

async function handleDelete(s: Station) {
  if (!confirm(`Remover a rádio "${s.name}"?`)) return;
  try {
    await deleteStation(s.id);
    await reload();
  } catch (err) {
    void openAlert({
      title: "Erro",
      message: "Falha ao remover: " + (friendlyError(err))
    });
  }
}

// =====================
// View
// =====================

export function PlayerStationsView() {
  useEffect(() => {
    void reload();
  }, []);

  if (loading.value && stations.value.length === 0) {
    return (
      <div class="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-md" />
        ))}
      </div>
    );
  }

  const term = search.value.toLowerCase().trim();
  const filtered = term
    ? stations.value.filter(
        (s) =>
          s.name.toLowerCase().includes(term) ||
          s.category.toLowerCase().includes(term) ||
          (s.genre ?? "").toLowerCase().includes(term) ||
          s.url.toLowerCase().includes(term)
      )
    : stations.value;

  // Agrupado por categoria pra facilitar visualização
  const grouped = new Map<string, Station[]>();
  for (const s of filtered) {
    const arr = grouped.get(s.category) ?? [];
    arr.push(s);
    grouped.set(s.category, arr);
  }

  return (
    <div class="space-y-4">
      {/* Barra de ações: busca + adicionar */}
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <label class="flex flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 focus-within:border-primary">
          <Search width="1.3em" height="1.3em" className="shrink-0 text-muted-foreground" />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar por nome, categoria, URL..."
            class="min-w-0 flex-1 bg-transparent py-2 text-sm placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </label>
        <Button
          onClick={() => (creating.value = true)}
          className="gap-1.5"
        >
          <Plus className="size-4" />
          Adicionar rádio
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/40 px-5 py-10 text-center text-sm text-muted-foreground">
          {term
            ? "Nenhuma rádio encontrada com esse termo."
            : "Nenhuma rádio cadastrada ainda."}
        </div>
      ) : (
        Array.from(grouped.entries()).map(([cat, items]) => (
          <section key={cat} class="space-y-1.5">
            <h2 class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {cat} <span class="text-muted-foreground/60">({items.length})</span>
            </h2>
            <div class="space-y-1.5">
              {items.map((s) => (
                <StationRow key={s.id} s={s} />
              ))}
            </div>
          </section>
        ))
      )}

      {creating.value && <StationModal onClose={() => (creating.value = false)} />}
      {editingId.value && (
        <StationModal
          station={stations.value.find((x) => x.id === editingId.value)}
          onClose={() => (editingId.value = null)}
        />
      )}
    </div>
  );
}

// =====================
// Row
// =====================

function StationRow({ s }: { s: Station }) {
  return (
    <div
      class={`flex items-center gap-3 rounded-md border border-border bg-card/50 px-3 py-2.5 transition-colors hover:bg-card ${
        s.isEnabled ? "" : "opacity-60"
      }`}
    >
      <div class="grid size-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
        <Music className="size-4" />
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5">
          <span class="truncate text-sm font-semibold">{s.name}</span>
          {!s.isEnabled && (
            <span class="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
              desligada
            </span>
          )}
        </div>
        <div class="truncate text-[11px] text-muted-foreground">{s.genre || "—"}</div>
        <div class="truncate font-mono text-[10px] text-muted-foreground/70">{s.url}</div>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => moveStation(s.id, -1)}
          title="Mover pra cima"
          aria-label="Mover pra cima"
          class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ArrowUp className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => moveStation(s.id, 1)}
          title="Mover pra baixo"
          aria-label="Mover pra baixo"
          class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ArrowDown className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => toggleEnabled(s)}
          title={s.isEnabled ? "Desligar rádio" : "Ligar rádio"}
          class={`grid size-7 place-items-center rounded-md transition-colors ${
            s.isEnabled
              ? "bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/30 dark:text-emerald-400"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          }`}
        >
          <Check className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => (editingId.value = s.id)}
          title="Editar"
          aria-label="Editar"
          class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => handleDelete(s)}
          title="Remover"
          aria-label="Remover rádio"
          class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

// =====================
// Modal: criar/editar
// =====================

function StationModal({
  station,
  onClose
}: {
  station?: Station;
  onClose: () => void;
}) {
  const isEdit = !!station;
  const [name, setName] = useState(station?.name ?? "");
  const [category, setCategory] = useState(station?.category ?? "");
  const [genre, setGenre] = useState(station?.genre ?? "");
  const [url, setUrl] = useState(station?.url ?? "");
  const [isEnabled, setIsEnabled] = useState(station?.isEnabled ?? true);
  const [detecting, setDetecting] = useState(false);
  const [detectMsg, setDetectMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDetect() {
    if (!url.trim()) {
      setDetectMsg("Digite uma URL primeiro.");
      return;
    }
    setDetecting(true);
    setDetectMsg(null);
    try {
      const res = await detectStation(url.trim());
      if (res.ok) {
        if (res.name && !name) setName(res.name);
        if (res.category && !category) setCategory(res.category);
        if (res.genre && !genre) setGenre(res.genre);
        setDetectMsg("Metadata detectada — confira os campos.");
      } else {
        setDetectMsg(res.message ?? "Não consegui detectar.");
      }
    } catch (err) {
      setDetectMsg(friendlyError(err));
    } finally {
      setDetecting(false);
    }
  }

  async function handleSave() {
    setError(null);
    if (!name.trim() || !category.trim() || !url.trim()) {
      setError("Nome, categoria e URL são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        category: category.trim(),
        genre: genre.trim() || null,
        url: url.trim(),
        isEnabled
      };
      if (isEdit && station) {
        await updateStation(station.id, body);
      } else {
        await createStation(body);
      }
      await reload();
      onClose();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[70] grid place-items-center bg-black/50 backdrop-blur-sm p-4"
      /* Modal admin não fecha por click no backdrop. [[feedback-modal-no-click-outside-close]] */
      role="dialog"
      aria-modal="true"
    >
      <div class="w-full max-w-md rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="text-sm font-bold">
            {isEdit ? "Editar rádio" : "Nova rádio"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="space-y-3 p-4">
          {/* URL primeiro pra incentivar detecção */}
          <div class="space-y-1">
            <label class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              URL do stream
            </label>
            <div class="flex gap-2">
              <input
                type="url"
                value={url}
                onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
                placeholder="https://exemplo.com/stream"
                class="min-w-0 flex-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleDetect}
                disabled={detecting || !url.trim()}
                className="shrink-0"
              >
                {detecting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  "Detectar"
                )}
              </Button>
            </div>
            {detectMsg && (
              <p class="text-[11px] text-muted-foreground">{detectMsg}</p>
            )}
          </div>

          <div class="space-y-1">
            <label class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Nome
            </label>
            <input
              type="text"
              value={name}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder="Ex: SomaFM Covers"
              class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <div class="space-y-1">
            <label class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Categoria
            </label>
            <input
              type="text"
              value={category}
              onInput={(e) => setCategory((e.target as HTMLInputElement).value)}
              placeholder="Ex: Lofi, Jazz / Vibe, Trance..."
              class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <div class="space-y-1">
            <label class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Gênero / Descrição
            </label>
            <input
              type="text"
              value={genre}
              onInput={(e) => setGenre((e.target as HTMLInputElement).value)}
              placeholder="Ex: Hip-hop instrumental / lofi beats"
              class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <label class="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-2 text-sm">
            <Switch
              checked={isEnabled}
              onCheckedChange={(v) => setIsEnabled(v)}
            />
            <span>Ligada (aparece no player do aluno)</span>
          </label>

          {error && (
            <div class="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : isEdit ? (
              "Salvar"
            ) : (
              "Criar"
            )}
          </Button>
        </footer>
      </div>
    </div>
  );
}
